import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Sparkles, Loader2, Send, MessageSquare, ChevronLeft, Search, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAIAssistantContext } from '@/contexts/AIAssistantContext';
import { useDMChat } from '@/hooks/useDMChat';
import { usePresence } from '@/hooks/usePresence';
import AIAssistantPanel from '@/components/ai-assistant/AIAssistantPanel';
import { formatDayMonth, formatTime } from '@/lib/dates';

const API_URL = import.meta.env.VITE_API_URL || '/api';

type PanelTab = 'ai' | 'messages';

interface DMConversation {
  id: string;
  type: string;
  subject: string | null;
  last_message_at: string;
  unread_count: number;
  is_muted: boolean;
  other_participants: { user_id: string; name: string; avatar_url: string | null }[] | null;
  last_message: { content: string; sender_name: string; created_at: string } | null;
}

export default function AIAssistant() {
  const { t } = useTranslation();
  const { isOpen, setIsOpen, setUnreadCount } = useAIAssistantContext();
  const [activeTab, setActiveTab] = useState<PanelTab>('ai');
  const [dmConversations, setDmConversations] = useState<DMConversation[]>([]);
  const [dmLoading, setDmLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [activeChatName, setActiveChatName] = useState('');
  const [activeChatAvatar, setActiveChatAvatar] = useState<string | null>(null);
  const [dmInput, setDmInput] = useState('');
  const [showNewDm, setShowNewDm] = useState(false);
  const [dmUserSearch, setDmUserSearch] = useState('');
  const [dmUserResults, setDmUserResults] = useState<{ id: string; name: string; username: string; avatar_url: string | null }[]>([]);
  const [dmSearching, setDmSearching] = useState(false);
  const [dmStarting, setDmStarting] = useState(false);
  const dmMessagesEndRef = useRef<HTMLDivElement>(null);
  const dmInputRef = useRef<HTMLInputElement>(null);

  // Sync unread count to context for sidebar badge
  useEffect(() => {
    setUnreadCount(totalUnread);
  }, [totalUnread, setUnreadCount]);

  // DM WebSocket hook for active chat
  const {
    messages: dmMessages,
    sendMessage: dmSendMessage,
    connected: dmConnected,
    typingUsers: dmTypingUsers,
    sendTyping: dmSendTyping,
    sendReadReceipt,
    currentUserId,
  } = useDMChat({ conversationId: activeChatId || '', enabled: !!activeChatId });

  // Presence tracking
  const { isOnline, checkStatus } = usePresence({ enabled: isOpen });

  // Fetch DM conversations
  const fetchDmConversations = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) return;
    setDmLoading(true);
    try {
      const res = await fetch(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const convs = data.conversations || [];
        setDmConversations(convs);
        setTotalUnread(convs.reduce((sum: number, c: DMConversation) => sum + c.unread_count, 0));
        const participantIds = convs
          .flatMap((c: DMConversation) => (c.other_participants || []).map(p => p.user_id))
          .filter(Boolean);
        if (participantIds.length > 0) checkStatus(participantIds);
      }
    } catch {} finally { setDmLoading(false); }
  }, [checkStatus]);

  // Fetch unread count periodically
  useEffect(() => {
    if (!isOpen) return;
    const fetchUnread = async () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/messages/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTotalUnread(data.unreadTotal || 0);
        }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Fetch conversations when messages tab is opened
  useEffect(() => {
    if (isOpen && activeTab === 'messages' && dmConversations.length === 0) {
      fetchDmConversations();
    }
  }, [isOpen, activeTab, dmConversations.length, fetchDmConversations]);

  // Auto-scroll DM messages
  useEffect(() => {
    if (activeChatId && dmMessagesEndRef.current) {
      dmMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dmMessages, activeChatId]);

  // DM typing debounce
  const dmTypingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleDmTyping = useCallback(() => {
    dmSendTyping(true);
    if (dmTypingTimer.current) clearTimeout(dmTypingTimer.current);
    dmTypingTimer.current = setTimeout(() => dmSendTyping(false), 2000);
  }, [dmSendTyping]);

  const handleDmSend = () => {
    if (!dmInput.trim() || !activeChatId) return;
    dmSendMessage(dmInput.trim());
    setDmInput('');
    if (dmTypingTimer.current) clearTimeout(dmTypingTimer.current);
    dmSendTyping(false);
  };

  const formatDmTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return formatDayMonth(date);
  };

  // Search users for new DM
  useEffect(() => {
    if (!dmUserSearch.trim() || dmUserSearch.length < 2) {
      setDmUserResults([]);
      return;
    }
    setDmSearching(true);
    const timeout = setTimeout(async () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/users/search?q=${encodeURIComponent(dmUserSearch)}&limit=8`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDmUserResults(data.users || []);
        }
      } catch {} finally { setDmSearching(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [dmUserSearch]);

  const startDmConversation = async (userId: string, userName: string, userAvatar: string | null) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token || dmStarting) return;
    setDmStarting(true);
    try {
      const res = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowNewDm(false);
        setDmUserSearch('');
        setDmUserResults([]);
        await fetchDmConversations();
        if (data.conversation?.id) {
          setActiveChatId(data.conversation.id);
          setActiveChatName(userName);
          setActiveChatAvatar(userAvatar);
        }
      }
    } catch {} finally { setDmStarting(false); }
  };

  const handleClose = () => {
    setIsOpen(false);
    setActiveChatId(null);
  };

  return (
    <>
      {/* Side Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50"
            onClick={handleClose}
          />

          {/* Panel */}
          <div className="fixed ltr:right-0 rtl:left-0 top-0 bottom-0 w-[380px] max-w-full bg-[#0a0a12] ltr:border-l rtl:border-r border-white/8 z-50 flex flex-col animate-slide-in-right overflow-x-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/8 bg-linear-to-r/srgb from-iris-500/20 to-iris-600/20 border-iris-500/20 flex-wrap gap-y-2">
              <div className="flex items-center gap-2">
                <Sparkles size={20} className="text-iris-400" />
                <div>
                  <h3 className="text-sm font-semibold text-white">{t('chromeAI.aiAssistant')}</h3>
                  <p className="text-xs text-[#a0a0b0]">Sol</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-lg text-[#a0a0b0] hover:text-white hover:bg-white/8 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tab Bar */}
            <div className="flex border-b border-white/8">
              <button
                onClick={() => setActiveTab('ai')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'ai'
                    ? 'text-iris-400 border-b-2 border-iris-400'
                    : 'text-[#9a9aac] hover:text-[#a0a0b0]'
                }`}
              >
                <Sparkles size={14} />
                AI
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'messages'
                    ? 'text-iris-400 border-b-2 border-iris-400'
                    : 'text-[#9a9aac] hover:text-[#a0a0b0]'
                }`}
              >
                <MessageSquare size={14} />
                Messages
                {totalUnread > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </button>
            </div>

            {/* === AI Tab === */}
            {activeTab === 'ai' && <AIAssistantPanel isOpen={isOpen} />}

            {/* === Messages Tab === */}
            {activeTab === 'messages' && <>
              {/* Conversation List (when no active chat) */}
              {!activeChatId ? (
                <div className="flex-1 overflow-y-auto flex flex-col">
                  {/* New Message Button / Search */}
                  {!showNewDm ? (
                    <button
                      onClick={() => setShowNewDm(true)}
                      className="flex items-center gap-2 mx-3 mt-3 mb-2 px-3 py-2.5 rounded-xl bg-iris-600/10 border border-iris-500/20 text-iris-400 hover:bg-iris-600/20 transition-colors text-sm font-medium"
                    >
                      <Plus size={16} />
                      New message
                    </button>
                  ) : (
                    <div className="mx-3 mt-3 mb-2">
                      <div className="relative">
                        <Search size={14} className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-[#9a9aac]" />
                        <input
                          type="text"
                          value={dmUserSearch}
                          onChange={e => setDmUserSearch(e.target.value)}
                          placeholder="Search people..."
                          autoFocus
                          className="w-full ltr:pl-9 rtl:pr-9 pr-8 py-2 rounded-lg bg-white/4 border border-white/8 text-sm text-white placeholder:text-[#9a9aac] focus:outline-hidden focus:border-iris-500/50"
                        />
                        <button
                          onClick={() => { setShowNewDm(false); setDmUserSearch(''); setDmUserResults([]); }}
                          className="absolute ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-[#9a9aac] hover:text-white"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      {/* Search Results */}
                      {dmSearching && (
                        <div className="flex justify-center py-3">
                          <Loader2 size={16} className="animate-spin text-[#9a9aac]" />
                        </div>
                      )}
                      {!dmSearching && dmUserSearch.length >= 2 && dmUserResults.length === 0 && (
                        <p className="text-center text-xs text-[#9a9aac] py-3">No users found</p>
                      )}
                      {dmUserResults.length > 0 && (
                        <div className="mt-1 border border-white/6 rounded-lg overflow-hidden">
                          {dmUserResults.map(user => (
                            <button
                              key={user.id}
                              onClick={() => startDmConversation(user.id, user.name, user.avatar_url)}
                              disabled={dmStarting}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/4 transition-colors disabled:opacity-50 border-b border-white/4 last:border-b-0"
                            >
                              {user.avatar_url ? (
                                <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-iris-600/30 flex items-center justify-center text-xs font-medium text-iris-300">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="text-left min-w-0">
                                <p className="text-sm text-white truncate">{user.name}</p>
                                <p className="text-[11px] text-[#9a9aac] truncate">@{user.username}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {dmLoading && dmConversations.length === 0 && (
                    <div className="flex items-center justify-center gap-2 py-8 text-[#a0a0b0]">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-xs">Loading conversations...</span>
                    </div>
                  )}

                  {!dmLoading && dmConversations.length === 0 && !showNewDm && (
                    <div className="text-center py-8 px-4">
                      <MessageSquare size={32} className="mx-auto mb-3 text-[#9a9aac]" />
                      <p className="text-sm text-[#a0a0b0] mb-1">No conversations yet</p>
                      <p className="text-xs text-[#9a9aac]">Tap &quot;New message&quot; to start chatting</p>
                    </div>
                  )}

                  {dmConversations.map(conv => {
                    const participant = conv.other_participants?.[0];
                    const name = participant?.name || 'Unknown';
                    const avatar = participant?.avatar_url;
                    const participantOnline = participant ? isOnline(participant.user_id) : false;

                    return (
                      <button
                        key={conv.id}
                        onClick={() => {
                          setActiveChatId(conv.id);
                          setActiveChatName(name);
                          setActiveChatAvatar(avatar || null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/4 transition-colors border-b border-white/4"
                      >
                        <div className="relative shrink-0">
                          {avatar ? (
                            <img src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-iris-600/30 flex items-center justify-center text-sm font-medium text-iris-300">
                              {name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {participantOnline && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-[#0d0d14]" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-semibold text-white' : 'text-[#e0e0e8]'}`}>
                              {name}
                            </span>
                            {conv.last_message && (
                              <span className="text-[10px] text-[#9a9aac] shrink-0">
                                {formatDmTime(conv.last_message.created_at)}
                              </span>
                            )}
                          </div>
                          {conv.last_message && (
                            <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? 'text-[#a0a0b0]' : 'text-[#9a9aac]'}`}>
                              {conv.last_message.content}
                            </p>
                          )}
                        </div>

                        {conv.unread_count > 0 && (
                          <span className="min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-iris-600 text-[10px] font-bold text-[#0C0C12] shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Active Chat View */
                <>
                  {/* Chat Header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8">
                    <button
                      onClick={() => {
                        setActiveChatId(null);
                        setDmInput('');
                        fetchDmConversations();
                      }}
                      className="p-1 rounded-lg text-[#a0a0b0] hover:text-white hover:bg-white/8 transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <div className="relative">
                      {activeChatAvatar ? (
                        <img src={activeChatAvatar} alt={activeChatName} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-iris-600/30 flex items-center justify-center text-xs font-medium text-iris-300">
                          {activeChatName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{activeChatName}</p>
                      <p className="text-[10px] text-[#9a9aac]">
                        {dmConnected ? (
                          <span className="text-emerald-400">connected</span>
                        ) : (
                          <span>connecting...</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {dmMessages.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-xs text-[#9a9aac]">No messages yet. Say hello!</p>
                      </div>
                    )}

                    {dmMessages.map(msg => {
                      const isMine = (msg.userId || msg.sender_id) === currentUserId;
                      return (
                        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                            isMine
                              ? 'bg-iris-600 text-[#0C0C12] rounded-br-sm'
                              : 'bg-white/6 text-[#e0e0e8] rounded-bl-sm'
                          }`}>
                            <p className="wrap-break-word">{msg.content}</p>
                            <p className={`text-[10px] mt-1 ${isMine ? 'text-iris-200/60' : 'text-[#9a9aac]'}`}>
                              {formatTime(msg.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                    {/* Typing indicator */}
                    {dmTypingUsers.length > 0 && (
                      <div className="flex items-center gap-2 text-[#9a9aac]">
                        <div className="flex gap-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#6e6e80] animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#6e6e80] animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#6e6e80] animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-[10px]">typing...</span>
                      </div>
                    )}

                    <div ref={dmMessagesEndRef} />
                  </div>

                  {/* Chat Input */}
                  <form
                    onSubmit={e => { e.preventDefault(); handleDmSend(); }}
                    className="p-4 border-t border-white/8"
                  >
                    <div className="flex gap-2">
                      <input
                        ref={dmInputRef}
                        type="text"
                        value={dmInput}
                        onChange={e => { setDmInput(e.target.value); handleDmTyping(); }}
                        placeholder="Type a message..."
                        className="flex-1 px-3 py-2 rounded-lg bg-white/2 border border-white/8 text-sm text-white placeholder:text-[#9a9aac] focus:outline-hidden focus:border-iris-500/50"
                      />
                      <button
                        type="submit"
                        disabled={!dmInput.trim()}
                        className="p-2 rounded-lg bg-iris-600 text-[#0C0C12] hover:bg-iris-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  </form>
                </>
              )}
            </>}
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        [dir="rtl"] .animate-slide-in-right {
          animation: slide-in-left 0.3s ease-out;
        }
        @keyframes slide-in-left {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
